
import React, { useState } from "react";
import { motion } from "framer-motion";
import { z } from 'zod';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Mail, Phone, Building, Send, Globe2, Activity, Wifi } from "lucide-react";
import TextReveal from "@/components/ui/TextReveal";
import GradientBackground from "@/components/ui/GradientBackground";

const contactSchema = z.object({
  fullName: z.string().min(3, "Please enter your full name."),
  email: z.string().email("Please enter a valid email address."),
  subject: z.string({ required_error: "Please select a subject." }),
  message: z.string().min(10, "Your message should be at least 10 characters long."),
});
type TContactSchema = z.infer<typeof contactSchema>;

// Simulated 3D Globe Component (CSS Animation)
const HolographicGlobe = () => (
  <div className="relative w-full aspect-square max-w-[500px] mx-auto perspective-[1000px]">
    {/* Globe Sphere - Wireframe effect */}
    <div className="absolute inset-0 rounded-full border border-primary/20 bg-primary/5 shadow-[0_0_50px_rgba(37,99,235,0.2)] animate-spin-slow"
      style={{ animationDuration: '20s' }}>
      {/* Grid Lines */}
      <div className="absolute inset-0 rounded-full border-t border-b border-primary/10 rotate-45" />
      <div className="absolute inset-0 rounded-full border-l border-r border-primary/10 -rotate-45" />
    </div>

    {/* Orbiting Dots */}
    {[...Array(5)].map((_, i) => (
      <div key={i} className={`absolute w-full h-full animate-spin-custom`} style={{ animationDuration: `${10 + i * 5}s`, animationDelay: `-${i * 2}s` }}>
        <div className="absolute top-0 left-1/2 w-3 h-3 bg-primary rounded-full shadow-[0_0_10px_#2563eb]" />
      </div>
    ))}

    {/* Floating Label */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center bg-black/80 backdrop-blur-md p-4 rounded-xl border border-primary/30">
      <Globe2 className="w-8 h-8 text-primary mx-auto mb-2 animate-pulse" />
      <div className="text-xs font-mono text-primary">STATUS: ONLINE</div>
      <div className="text-white font-bold text-lg tracking-widest">K&C HQ</div>
    </div>
  </div>
);

const ContactPage = () => {
  const form = useForm<TContactSchema>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      fullName: "",
      email: "",
      subject: undefined, // Controlled, but no default selection
      message: "",
    },
  });

  const onSubmit = (data: TContactSchema) => {
    console.log("Contact Form Submitted:", data);
    toast.success("Message received. We'll deploy a response shortly.");
    form.reset({ fullName: '', email: '', subject: undefined, message: '' });
  };

  return (
    <div className="bg-background text-foreground relative min-h-screen overflow-hidden font-sans selection:bg-primary/30">
      <GradientBackground />

      <div className="container mx-auto px-4 py-32 relative z-10">

        {/* Header */}
        <div className="text-center mb-24">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-6 animate-pulse">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase flex items-center gap-2">
              <Wifi className="w-4 h-4" /> Secure Channel Open
            </span>
          </div>
          <TextReveal className="text-6xl md:text-9xl font-black tracking-tighter text-foreground leading-[0.9] mb-8">
            TRANSMIT.
          </TextReveal>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto"
          >
            Direct line to our logistics command center. We are standing by.
          </motion.p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center max-w-7xl mx-auto">

          {/* Left Column: Holographic Globe & Info */}
          <div className="space-y-12 order-2 lg:order-1">
            <HolographicGlobe />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { icon: Mail, title: "Digital", desc: "support@kclogistics.com", href: "mailto:ymaluwa@gmail.com" },
                { icon: Phone, title: "Voice", desc: "+265 994 124 06", href: "tel:+265994912406" },
              ].map((item, i) => (
                <motion.a
                  key={i}
                  href={item.href}
                  whileHover={{ scale: 1.05 }}
                  className="p-6 rounded-2xl bg-foreground/5 border border-foreground/10 hover:border-primary/50 hover:bg-primary/5 transition-all text-center group cursor-pointer"
                >
                  <item.icon className="w-8 h-8 text-primary mx-auto mb-4 group-hover:scale-110 transition-transform" />
                  <h4 className="font-bold text-lg mb-1">{item.title}</h4>
                  <p className="text-muted-foreground text-sm">{item.desc}</p>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="p-8 md:p-12 rounded-[2.5rem] bg-background border border-foreground/10 shadow-2xl relative overflow-hidden order-1 lg:order-2"
          >
            {/* Form Background Accent */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -z-10" />

            <div className="mb-10 flex items-center justify-between">
              <div>
                <h3 className="text-3xl font-bold mb-2">Message Payload</h3>
                <p className="text-muted-foreground">Encrypted transmission.</p>
              </div>
              <Activity className="w-8 h-8 text-primary animate-pulse" />
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <FormField control={form.control} name="fullName" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="uppercase text-xs font-bold tracking-wider text-muted-foreground">Identity</FormLabel>
                      <FormControl>
                        <Input placeholder="Full Name" {...field} className="h-14 bg-foreground/5 border-transparent focus:border-primary/50 focus:bg-background text-lg rounded-xl transition-all" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="email" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="uppercase text-xs font-bold tracking-wider text-muted-foreground">Signal Frequency</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="Email Address" {...field} className="h-14 bg-foreground/5 border-transparent focus:border-primary/50 focus:bg-background text-lg rounded-xl transition-all" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                <FormField control={form.control} name="subject" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="uppercase text-xs font-bold tracking-wider text-muted-foreground">Topic</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-14 bg-foreground/5 border-transparent focus:ring-primary/50 focus:bg-background rounded-xl text-lg">
                          <SelectValue placeholder="Select Inquiry Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="client-support">Client Support</SelectItem>
                        <SelectItem value="transporter-support">Transporter Support</SelectItem>
                        <SelectItem value="billing-inquiry">Billing Inquiry</SelectItem>
                        <SelectItem value="general-inquiry">General Inquiry</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="message" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="uppercase text-xs font-bold tracking-wider text-muted-foreground">Payload</FormLabel>
                    <FormControl>
                      <Textarea rows={6} placeholder="How can we assist?" {...field} className="bg-foreground/5 border-transparent focus:border-primary/50 focus:bg-background text-lg rounded-xl resize-none p-4 transition-all" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />

                <Button type="submit" size="lg" className="w-full h-16 text-xl font-bold rounded-xl shadow-lg hover:shadow-primary/25 transition-all" disabled={form.formState.isSubmitting}>
                  Transmit Message <Send className="ml-2 h-6 w-6" />
                </Button>
              </form>
            </Form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
export default ContactPage;